/**
 * inspectionUploadQueue - Fila de envio de evidências em segundo plano (vistoria externa).
 *
 * Regras:
 * - Até `concurrency` arquivos sobem ao mesmo tempo (padrão 2, para não travar celulares fracos).
 * - A gravação da resposta é serializada por item (answerId): nunca duas gravações simultâneas
 *   do mesmo item, evitando que uma foto sobrescreva a outra. Arquivos já enviados e ainda não
 *   gravados do mesmo item são gravados juntos.
 * - Falha de envio tenta de novo até `maxAttempts` vezes com espera crescente; depois fica
 *   em erro até o usuário reenviar (`retry`) ou a internet voltar (`retryAll`).
 * - Arquivo enviado mas não gravado continua contando como pendente.
 * - `restore` reinsere tarefas guardadas no aparelho; as que já tinham sido enviadas só
 *   regravam a resposta (o arquivo não sobe de novo).
 *
 * Módulo puro (sem React) para ser testável; o hook `useInspectionUploadQueue` o envolve.
 *
 * RELEASE: 2026-10-14 v1.1.0 - Progresso por tarefa (0..1), ganchos de persistência
 *          (onAdded/onUploaded/onDone) e `restore`.
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */

export type UploadStatus = 'pending' | 'uploading' | 'saving' | 'done' | 'error';

export interface UploadTask<A = unknown> {
  id: string;
  answerId: string;
  file: File;
  fromCamera: boolean;
  previewUrl: string | null;
  status: UploadStatus;
  attempts: number;
  error: string | null;
  /** Progresso do envio do arquivo (0..1). */
  progress: number;
  /** Metadados devolvidos pelo envio (antes da gravação da resposta). */
  uploaded: A | null;
}

export interface RestoreEntry<A> {
  id: string;
  answerId: string;
  file: File;
  fromCamera: boolean;
  uploaded: A | null;
  previewUrl?: string | null;
}

export interface QueueDeps<A> {
  /** Envia o arquivo, informa o progresso e devolve os metadados do anexo. */
  upload: (task: UploadTask<A>, onProgress: (p: number) => void) => Promise<A>;
  /** Grava a resposta do item com os novos anexos (servidor recebe vigentes + novos). */
  save: (answerId: string, attachments: A[]) => Promise<void>;
  onChange: (tasks: UploadTask<A>[]) => void;
  /** Ganchos de persistência (opcionais). */
  onAdded?: (task: UploadTask<A>) => void;
  onUploaded?: (task: UploadTask<A>) => void;
  onDone?: (task: UploadTask<A>) => void;
  concurrency?: number;
  maxAttempts?: number;
  /** Espera antes da nova tentativa (ms); injetável nos testes. */
  delay?: (attempt: number) => Promise<void>;
  /** Intervalo mínimo entre avisos de progresso (ms). */
  progressThrottleMs?: number;
}

const defaultDelay = (attempt: number) => new Promise<void>((r) => setTimeout(r, Math.min(8000, 1000 * 2 ** (attempt - 1))));

/** Progresso agregado (0..1): concluído/gravando conta 1, erro conta 0. */
export function aggregateProgress(tasks: Pick<UploadTask, 'status' | 'progress'>[]) {
  if (!tasks.length) return 0;
  const sum = tasks.reduce((s, t) => s + (t.status === 'done' || t.status === 'saving' ? 1 : t.status === 'error' ? 0 : t.progress), 0);
  return sum / tasks.length;
}

export class InspectionUploadQueue<A = unknown> {
  private tasks: UploadTask<A>[] = [];
  private active = 0;
  private savingAnswers = new Set<string>();
  private seq = 0;
  private lastProgressEmit = 0;

  constructor(private deps: QueueDeps<A>) {}

  get list() { return this.tasks; }

  /** Pendências que impedem enviar a vistoria/assinar o aceite (inclui erros). */
  get hasPending() { return this.tasks.some((t) => t.status !== 'done'); }

  add(answerId: string, files: File[], fromCamera: boolean, previewUrl?: (f: File) => string | null) {
    for (const file of files) {
      const t: UploadTask<A> = {
        id: `u${Date.now().toString(36)}${++this.seq}`, answerId, file, fromCamera, previewUrl: previewUrl?.(file) ?? null,
        status: 'pending', attempts: 0, error: null, progress: 0, uploaded: null,
      };
      this.tasks.push(t);
      this.deps.onAdded?.(t);
    }
    this.emit();
    this.pump();
  }

  /** Reinsere tarefas guardadas no aparelho (ignora ids já presentes). */
  restore(entries: RestoreEntry<A>[]) {
    const answers = new Set<string>();
    for (const e of entries) {
      if (this.tasks.some((t) => t.id === e.id)) continue;
      this.tasks.push({
        id: e.id, answerId: e.answerId, file: e.file, fromCamera: e.fromCamera, previewUrl: e.previewUrl ?? null,
        status: e.uploaded ? 'saving' : 'pending', attempts: 0, error: null, progress: e.uploaded ? 1 : 0, uploaded: e.uploaded,
      });
      if (e.uploaded) answers.add(e.answerId);
    }
    this.emit();
    this.pump();
    answers.forEach((a) => void this.flushSave(a));
  }

  /** Reenvia uma tarefa com erro. */
  retry(id: string) {
    const t = this.tasks.find((x) => x.id === id);
    if (!t || t.status !== 'error') return;
    t.status = t.uploaded ? 'saving' : 'pending';
    t.attempts = 0; t.error = null; t.progress = t.uploaded ? 1 : 0;
    this.emit();
    if (t.uploaded) void this.flushSave(t.answerId); else this.pump();
  }

  retryAll() { this.tasks.filter((t) => t.status === 'error').forEach((t) => this.retry(t.id)); }

  /** Remove tarefas concluídas (após o recarregamento já mostrar os anexos). */
  clearDone() {
    const before = this.tasks.length;
    this.tasks = this.tasks.filter((t) => t.status !== 'done');
    if (this.tasks.length !== before) this.emit();
  }

  private emit() { this.deps.onChange([...this.tasks.map((t) => ({ ...t }))]); }

  private pump() {
    const limit = this.deps.concurrency ?? 2;
    while (this.active < limit) {
      const next = this.tasks.find((t) => t.status === 'pending');
      if (!next) return;
      void this.runUpload(next);
    }
  }

  private async runUpload(t: UploadTask<A>) {
    this.active++;
    t.status = 'uploading';
    t.progress = 0;
    this.emit();
    const max = this.deps.maxAttempts ?? 3;
    const throttle = this.deps.progressThrottleMs ?? 250;
    const onProgress = (p: number) => {
      t.progress = Math.max(0, Math.min(1, p));
      const now = Date.now();
      if (now - this.lastProgressEmit >= throttle) { this.lastProgressEmit = now; this.emit(); }
    };
    while (true) {
      t.attempts++;
      try {
        t.uploaded = await this.deps.upload(t, onProgress);
        t.status = 'saving';
        t.progress = 1;
        this.deps.onUploaded?.(t);
        break;
      } catch (e) {
        if (t.attempts >= max) { t.status = 'error'; t.error = (e as Error).message || 'Falha ao enviar'; break; }
        t.progress = 0;
        await (this.deps.delay ?? defaultDelay)(t.attempts);
      }
    }
    this.active--;
    this.emit();
    this.pump();
    if (t.status === 'saving') await this.flushSave(t.answerId);
  }

  /** Grava, em série por item, todos os arquivos já enviados e ainda não gravados. */
  private async flushSave(answerId: string) {
    if (this.savingAnswers.has(answerId)) return;
    this.savingAnswers.add(answerId);
    try {
      while (true) {
        const batch = this.tasks.filter((t) => t.answerId === answerId && t.status === 'saving' && t.uploaded);
        if (!batch.length) break;
        try {
          await this.deps.save(answerId, batch.map((t) => t.uploaded as A));
          batch.forEach((t) => { t.status = 'done'; this.deps.onDone?.(t); });
        } catch (e) {
          batch.forEach((t) => { t.status = 'error'; t.error = (e as Error).message || 'Falha ao salvar'; });
          this.emit();
          break;
        }
        this.emit();
      }
    } finally {
      this.savingAnswers.delete(answerId);
    }
  }
}
